import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileSearch, ArrowLeft, Upload, CheckCircle2, XCircle, AlertTriangle, FileText, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

export default function InvoiceAuditorPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [invoiceData, setInvoiceData] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputMode, setInputMode] = useState('text'); // 'text' or 'file'

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      toast.success(`Wybrano: ${selectedFile.name}`);
    }
  };

  const handleAudit = async () => {
    if (!invoiceData && !file) {
      toast.error('Wprowadź dane faktury lub wgraj plik');
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (inputMode === 'file' && file) {
        const formData = new FormData();
        formData.append('file', file);
        response = await axios.post(`${BACKEND_URL}/api/invoice/audit-file`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        });
      } else {
        response = await axios.post(`${BACKEND_URL}/api/invoice/audit`, {
          invoice_data: invoiceData
        }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          timeout: 60000
        });
      }
      
      setResult(response.data);
      toast.success('Analiza zakończona!');
    } catch (error) {
      console.error('Audit error:', error);
      toast.error('Błąd analizy faktury');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <FileText className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'ok': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      case 'error': return 'bg-red-50 border-red-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      {/* Header */}
      <div className="border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')} 
              data-testid="back-to-dashboard"
              className="gap-2 text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="w-4 h-4" /> Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-200 dark:shadow-none">
                <FileSearch className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 dark:text-white">Audytor Faktur</h1>
                <p className="text-xs text-slate-400">Sprawdź poprawność faktury</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Input */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="p-5 bg-gradient-to-r from-orange-500 to-red-600">
              <h2 className="font-bold text-white text-lg flex items-center gap-2">
                <FileSearch className="w-5 h-5" />
                Dane faktury do sprawdzenia
              </h2>
              <p className="text-white/80 text-sm mt-1">Wklej dane lub wgraj plik faktury</p>
            </div>
            
            <div className="p-5">
              {/* Mode selector */}
              <div className="flex gap-2 mb-4">
                <Button
                  variant={inputMode === 'text' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInputMode('text')}
                  className={inputMode === 'text' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  <FileText className="w-4 h-4 mr-1" /> Wpisz dane
                </Button>
                <Button
                  variant={inputMode === 'file' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInputMode('file')}
                  className={inputMode === 'file' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  <Upload className="w-4 h-4 mr-1" /> Wgraj plik
                </Button>
              </div>

              {inputMode === 'text' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Wprowadź dane faktury
                  </label>
                  <textarea
                    data-testid="invoice-data-input"
                    value={invoiceData}
                    onChange={(e) => setInvoiceData(e.target.value)}
                    className="w-full h-64 p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm resize-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                    placeholder={`Przykład:
Faktura VAT nr: FV/2026/001
Data wystawienia: 15.02.2026
Data sprzedaży: 15.02.2026
Sprzedawca: ABC Sp. z o.o., NIP: 1234567890
Nabywca: XYZ Sp. z o.o., NIP: 0987654321
Pozycje:
1. Usługa konsultingowa - 1000 zł netto, VAT 23%
Razem netto: 1000 zł
VAT: 230 zł
Brutto: 1230 zł`}
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Wgraj fakturę (PDF, obraz)
                  </label>
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center hover:border-orange-400 transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="hidden"
                      id="invoice-file"
                    />
                    <label htmlFor="invoice-file" className="cursor-pointer">
                      <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {file ? file.name : 'Kliknij aby wybrać plik'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG (max 10MB)</p>
                    </label>
                  </div>
                </div>
              )}

              <Button
                data-testid="audit-invoice-btn"
                onClick={handleAudit}
                disabled={loading || (!invoiceData && !file)}
                className="w-full mt-5 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white h-12 rounded-xl shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analizuję fakturę...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" /> Sprawdź fakturę
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Right: Results */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 dark:border-slate-700">
              <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Wynik audytu
              </h3>
            </div>
            
            {result ? (
              <ScrollArea className="h-[500px]">
                <div className="p-5 space-y-4">
                  {/* Overall status */}
                  <div className={`p-4 rounded-xl border ${getStatusBg(result.overall_status)}`}>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(result.overall_status)}
                      <div>
                        <h4 className="font-bold text-lg">
                          {result.overall_status === 'ok' ? 'Faktura poprawna' :
                           result.overall_status === 'warning' ? 'Uwagi do faktury' :
                           'Błędy w fakturze'}
                        </h4>
                        <p className="text-sm text-slate-600">{result.summary}</p>
                      </div>
                    </div>
                  </div>

                  {/* Check items */}
                  {result.checks && result.checks.map((check, idx) => (
                    <div key={idx} className={`p-4 rounded-xl border ${getStatusBg(check.status)}`}>
                      <div className="flex items-start gap-3">
                        {getStatusIcon(check.status)}
                        <div>
                          <h5 className="font-semibold text-slate-800 dark:text-slate-100">{check.name}</h5>
                          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{check.description}</p>
                          {check.recommendation && (
                            <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 font-medium">
                              💡 {check.recommendation}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Legal basis */}
                  {result.legal_basis && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                      <h5 className="font-semibold text-slate-700 dark:text-slate-200 mb-2">📜 Podstawa prawna</h5>
                      <p className="text-sm text-slate-600 dark:text-slate-300">{result.legal_basis}</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="h-[400px] flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-20 h-20 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                    <FileSearch className="w-10 h-10 text-slate-300 dark:text-slate-500" />
                  </div>
                  <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Brak wyników</h4>
                  <p className="text-sm text-slate-400 max-w-xs">
                    Wprowadź dane faktury i kliknij "Sprawdź fakturę" aby zobaczyć wyniki audytu
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info section */}
        <div className="mt-6 p-5 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl border border-orange-100 dark:border-orange-800">
          <h4 className="font-semibold text-slate-800 dark:text-white mb-3">🔍 Co sprawdza Audytor Faktur?</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300">Poprawność danych sprzedawcy i nabywcy (NIP, adres)</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300">Prawidłowość stawek VAT i obliczeń</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300">Elementy wymagane przez ustawę o VAT</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300">Daty wystawienia i sprzedaży</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300">Numeracja faktur</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300">Zgodność z wymogami KSeF</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
